// Own stationed ships under own systems at the close tier (spec 15c §3.4): glyph + count per class.
// A row shows and hides with its system's label (15c feedback, `declutter.ts`).
import type { MapSystem, ShipCounts } from '@stars-and-ships/api-types';
import { Container, Graphics, Text } from 'pixi.js';
import { SHIP_ORDER } from '../../labels';
import { MAP_COLORS } from '../../theme';
import { type Candidate, labelKey, type Offset } from '../declutter';
import { formatShipCount } from '../garrisons';
import { placeGlyph, SHIP_GLYPHS } from '../glyphs';
import { dotRadiusPx, type ZoomTier } from '../zoomTier';
import { LABEL_FONT, LABEL_GAP_PX, LABEL_STROKE } from './labels';

const GLYPH_PX = 5;
/** Below the two label lines. */
const ROW_BELOW_LABEL_PX = 30;
const GAP_PX = 8;

export interface GarrisonsLayer {
  container: Container;
  setView(tier: ZoomTier, scale: number): void;
  /** Screen rectangles of the rows, keyed like their system's label. */
  candidates(priority: (system: MapSystem) => number): Candidate[];
  applyShown(shown: ReadonlyMap<string, Offset>): void;
}

export function garrisonsLayer(input: {
  systems: readonly MapSystem[];
  garrisons: ReadonlyMap<string, ShipCounts>;
}): GarrisonsLayer {
  const container = new Container();
  const entries: { system: MapSystem; box: Container; row: Container }[] = [];
  for (const s of input.systems) {
    const counts = input.garrisons.get(s.id);
    if (!counts) continue;
    const box = new Container();
    box.position.set(s.x, s.y);
    const row = new Container();
    let x = 0;
    for (const type of SHIP_ORDER) {
      const n = counts[type];
      if (!n) continue;
      const glyph = new Graphics()
        .poly(placeGlyph(SHIP_GLYPHS[type], x + GLYPH_PX, GLYPH_PX + 1, GLYPH_PX))
        .fill(MAP_COLORS.own);
      const label = new Text({
        text: formatShipCount(n),
        style: {
          fill: MAP_COLORS.label,
          fontSize: 10,
          fontFamily: LABEL_FONT,
          stroke: LABEL_STROKE,
        },
      });
      label.position.set(x + GLYPH_PX * 2 + 2, 0);
      row.addChild(glyph, label);
      x = label.x + label.width + GAP_PX;
    }
    row.x = -(x - GAP_PX) / 2;
    box.addChild(row);
    container.addChild(box);
    entries.push({ system: s, box, row });
  }
  let viewScale = 1;
  return {
    container,
    setView(tier, scale) {
      viewScale = scale;
      container.visible = tier === 'close';
      const y = dotRadiusPx(scale) + LABEL_GAP_PX + ROW_BELOW_LABEL_PX;
      for (const { box, row } of entries) {
        box.scale.set(1 / scale);
        row.y = y;
      }
    },
    candidates(priority) {
      if (!container.visible) return [];
      return entries.map(({ system, row }) => ({
        key: labelKey(system.id),
        systemId: system.id,
        priority: priority(system),
        rects: [
          {
            x: system.x * viewScale + row.x,
            y: system.y * viewScale + row.y,
            w: row.width,
            h: row.height,
          },
        ],
      }));
    },
    applyShown(shown) {
      for (const { system, box } of entries) box.visible = shown.has(labelKey(system.id));
    },
  };
}
